import { useState } from 'react';
import { Sparkles, X, Newspaper, Target, HelpCircle, Send } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { apiClient } from '../../lib/api';

const SUGGESTIONS_BRIEF = [
  "What's the biggest risk to watch today?",
  "Which stocks are most affected by the Samsung injunction?",
];
const SUGGESTIONS_STOCK = sym => [
  `What's the biggest risk to the ${sym} bull case?`,
  `How does the Fed transition affect ${sym} multiples?`,
];

export default function FloatingAIPanel({ context = 'brief' }) {
  const { user } = useAuth();
  const [open, setOpen]   = useState(false);
  const [msgs, setMsgs]   = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  const isBrief = context === 'brief';
  const ctxLabel = isBrief ? 'Daily Brief · Today' : `${context} · Deep Dive · Today`;
  const suggestions = isBrief ? SUGGESTIONS_BRIEF : SUGGESTIONS_STOCK(context);

  async function send(text) {
    if (!text.trim()) return;
    const time = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    setMsgs(prev => [...prev, { role: 'user', text, time }]);
    setInput('');
    setLoading(true);
    try {
      const token = await user?.getIdToken?.();
      const res = await apiClient.post(
        '/api/ai/chat',
        { message: text, context },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setMsgs(prev => [...prev, { role: 'ai', text: res.data.reply, time }]);
    } catch {
      setMsgs(prev => [...prev, { role: 'ai', text: "Couldn't reach the AI service. Try again.", time: '' }]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className={`ai-backdrop${open ? ' show' : ''}`} onClick={() => setOpen(false)} />

      <div className={`ai-float-panel${open ? ' open' : ''}`}>
        <div className="ai-panel-hdr">
          <div className="ai-panel-icon"><Sparkles size={15} /></div>
          <div style={{ flex: 1 }}>
            <div className="ai-panel-name">MIG Research Assistant</div>
            <div className="ai-panel-sub">Grounded in today's full report</div>
          </div>
          <button className="ai-panel-close" onClick={() => setOpen(false)}>
            <X size={16} />
          </button>
        </div>

        <div className="ai-panel-ctx">
          {isBrief ? <Newspaper size={12} /> : <Target size={12} />}
          {ctxLabel}
        </div>

        <div className="ai-msgs">
          {msgs.length === 0 && (
            <div className="msg ai">
              <div className="msg-bubble">
                {isBrief
                  ? "I've analyzed today's brief. Ask me anything about the Samsung injunction, Fed transition, or any stock in focus."
                  : `I've loaded the full ${context} report. Ask me about projections, risks, or sources.`}
              </div>
              <div className="msg-time">MIG AI · now</div>
            </div>
          )}
          {msgs.map((m, i) => (
            <div key={i} className={`msg ${m.role}`}>
              <div className="msg-bubble" dangerouslySetInnerHTML={{ __html: m.text }} />
              {m.time && <div className="msg-time">{m.role === 'user' ? 'You' : 'MIG AI'} · {m.time}</div>}
            </div>
          ))}
          {loading && (
            <div className="msg ai">
              <div className="msg-bubble" style={{ color: 'var(--muted)' }}>Analyzing report context…</div>
            </div>
          )}
        </div>

        <div className="ai-sugg">
          <div className="ai-sugg-lbl">Suggested</div>
          {suggestions.map(q => (
            <button key={q} className="ai-chip" onClick={() => send(q)}>
              <HelpCircle size={12} /> {q}
            </button>
          ))}
        </div>

        <div className="ai-inp-area">
          <div className="ai-inp-wrap">
            <input
              className="ai-inp"
              type="text"
              placeholder="Ask about today's brief or any stock…"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') send(input); }}
            />
            <button className="ai-send" onClick={() => send(input)}>
              <Send size={13} />
            </button>
          </div>
          <div className="ai-meta">Full brief · sources grounded · claims labeled</div>
        </div>
      </div>

      <button className={`ai-fab${open ? ' open' : ''}`} onClick={() => setOpen(v => !v)}>
        <div className="ai-fab-icon">
          {open ? <X size={13} /> : <Sparkles size={13} />}
        </div>
        {open ? 'Close' : 'MIG AI'}
      </button>
    </>
  );
}
